import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import HailIcon from '@mui/icons-material/Hail';
import WorkIcon from '@mui/icons-material/Work';
import ChatBubble from '@mui/icons-material/ChatBubble';

import { Application } from '@libs/models';
import { APPLICATION_INDEX_COLUMN_INITIAL_STATE, createApplicationIndexColumns, routes } from '@carcareers/utils';
import { DashboardPageContainer, DataGridV2 as DataGrid } from '@carcareers/components';
import { useChat, useSnackbar } from '@carcareers/contexts';
import { useEmployerApplicationsSearchQuery } from '@carcareers/store';

export const EmployerJobseekerIndex: FC = () => {
  const navigate = useNavigate();
  const search = useEmployerApplicationsSearchQuery;
  const { employerChatAction } = useChat();
  const { toggleSnackbar } = useSnackbar();

  const baseColumns = useMemo(() => createApplicationIndexColumns(false), []);

  const columns = useMemo(
    () => [
      ...baseColumns,
      {
        field: 'actions',
        type: 'actions' as const,
        flex: 1,
        align: 'right' as const,
        headerAlign: 'right' as const,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            showInMenu
            key={`view-candidate-${params.row.id}`}
            label="View Candidate"
            icon={<HailIcon />}
            onClick={() => {
              const row = params.row as Application;
              if (row?.jobseeker?.id) {
                navigate(
                  `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_CANDIDATES_VIEW.replace(
                    ':id',
                    row.jobseeker.id.toString(),
                  )}`,
                );
              }
            }}
          />,
          <GridActionsCellItem
            showInMenu
            key={`view-post-${params.row.id}`}
            label="View Job"
            icon={<WorkIcon />}
            onClick={() => {
              const row = params.row as Application;
              if (row?.post?.id) {
                navigate(
                  `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_POSTS_VIEW.replace(':id', row.post.id.toString())}`,
                );
              }
            }}
          />,
          <GridActionsCellItem
            showInMenu
            disabled={params.row.jobseeker?.isExternalApply}
            key={`view-chat-${params.row.id}`}
            label="View Chat"
            icon={<ChatBubble />}
            onClick={async () => {
              const row = params.row as Application;
              if (row?.jobseeker?.user?.id && row?.post?.id && row?.post?.job?.title) {
                await employerChatAction(
                  row.post.id,
                  row.jobseeker?.user?.id,
                  row.jobseeker?.user?.firstName + ' ' + row.jobseeker?.user?.lastName,
                  row.post.job.title,
                  row.post.job.rooftop?.name,
                );
              } else {
                toggleSnackbar('error', 'Failed to start chat');
              }
            }}
          />,
        ],
      },
    ],
    [baseColumns, navigate, employerChatAction, toggleSnackbar],
  );

  return (
    <DashboardPageContainer>
      <DataGrid<Application>
        query={search}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: APPLICATION_INDEX_COLUMN_INITIAL_STATE,
          },
        }}
      />
    </DashboardPageContainer>
  );
};

export default EmployerJobseekerIndex;
